import { AfterViewInit, Component, EventEmitter, Input, OnChanges, Output } from '@angular/core';
import { UntypedFormControl, Validators } from '@angular/forms';
import { isNumber } from 'lodash-es';
import { createForm, FormType, subformComponentProviders } from 'ngx-sub-form';
import { UserUnitsModel } from 'src/app/core/components/user-units/user-units.model';
import { unitsLib } from 'src/app/core/services/unit-library';
import { Flow } from 'src/app/perical/models/flow.model';
import { Fluid, FluidType } from 'src/app/perical/models/fluid.model';
import { CirculationOperation, MudPit, OperationType } from 'src/app/perical/models/thermal-operation.model';
import { GetShortLengthValueFromInches } from 'src/app/perivis/shared/helpers/units.helper';

export interface CirculationOperationForm {
  type: OperationType;
  fluid: Fluid;
  circulationDirection: string;
  pressure: number;
  pressureLocation: string;
  circulationMd: number;
  inletTemperature: number;
  standardFluidFlow: Flow;
  useMudPits: boolean;
  mudPits: any;
  includeRiser: boolean;
  outsideDiameter: number;
  insideDiameter: number;
  annulusIndex: number;
}

@Component({
    selector: 'app-circulation-operation-details',
    templateUrl: './circulation-operation-details.component.html',
    providers: subformComponentProviders(CirculationOperationDetailsComponent),
    standalone: false
})
export class CirculationOperationDetailsComponent implements OnChanges, AfterViewInit {

  @Input()
  public isReadOnly: boolean;

  @Input()
  public userUnits: UserUnitsModel;

  @Input()
  public surfaceTemperature: number;

  @Input()
  public maxCircDepth: number;

  @Input()
  public previousOperationMudPits: MudPit;

  @Input()
  public currentTubularIndex: number;

  @Input()
  public isSubsea: boolean;

  @Input()
  public drillString: any;

  @Input()
  public defaultRiser: any;

  @Input()
  public showLandingString: boolean;

  @Input()
  public hangerMd: number;

  @Output()
  fluidChanged = new EventEmitter<{ hasGasProperty: boolean, hasOilProperty: boolean, hasWaterMudProperty: boolean }>();

  @Output()
  selectedTabChange = new EventEmitter<string>();

  public fluids: Fluid[];
  @Input('fluids') set _fluids(fluids: Fluid[]) {
    this.fluids = fluids?.filter(f => f.state.type === FluidType.BRINE ||
      f.state.type === FluidType.STANDARDMUD ||
      f.state.type === FluidType.ADVANCEDMUD ||
      f.state.type === FluidType.CEMENTSLURRY)
      .sort((a, b) => a.state.name.localeCompare(b.state.name));
  }
  public FluidType: typeof FluidType = FluidType;

  public circulationDirections = [
    { name: 'Forward', value: 'forward' },
    { name: 'Reverse', value: 'reverse' }
  ];
  public pressureLocations = [
    { name: 'Choke', value: 'choke' },
    { name: 'Pump', value: 'pump' }
  ];

  public depthUnit: string;
  public tempUnit: string;
  public pipeRoughnessUnit: string;
  public pressureUnit: string;
  public temperatureValidation: { min: number, max: number };
  public pressureValidation: { min: number, max: number };
  public useMudPits = false;
  public showRiser: boolean;
  public selectedLandingString: string;
  public landingStringMissing: boolean;

  private _dataBackup: any | null = null;

  public form = createForm<CirculationOperation, CirculationOperationForm>(this, {
    formType: FormType.SUB,
    formControls: {
      type: new UntypedFormControl(OperationType.CIRCULATION),
      fluid: new UntypedFormControl(null, [Validators.required]),
      circulationDirection: new UntypedFormControl('forward', [Validators.required]),
      pressure: new UntypedFormControl(null, { updateOn: 'blur' }),
      pressureLocation: new UntypedFormControl('choke', [Validators.required]),
      circulationMd: new UntypedFormControl(null, { updateOn: 'blur' }),
      inletTemperature: new UntypedFormControl(null, { updateOn: 'blur' }),
      standardFluidFlow: new UntypedFormControl(null),
      useMudPits: new UntypedFormControl(false),
      mudPits: new UntypedFormControl(null),
      includeRiser: new UntypedFormControl(null),
      outsideDiameter: new UntypedFormControl(null, { updateOn: 'blur' }),
      insideDiameter: new UntypedFormControl(null, { updateOn: 'blur' }),
      annulusIndex: new UntypedFormControl(null),
    },
    toFormGroup: (obj: CirculationOperation | null): CirculationOperationForm | null => {
      if (!obj) {
        return null;
      }

      this._dataBackup = { ...obj };

      this.useMudPits = obj.useMudPits;

      this.showRiser = obj.includeRiser;

      return {
        type: obj.type,
        fluid: this.fluids?.find(f => f.id === obj.fluid.id),
        standardFluidFlow: obj.flow,
        circulationDirection: obj.circulationParameters.circulationDirection,
        circulationMd: obj.circulationParameters.circulationMd ?? this.maxCircDepth,
        inletTemperature: obj.circulationParameters.inletTemperature ?? this.surfaceTemperature,
        pressure: obj.circulationParameters.pressure,
        pressureLocation: obj.circulationParameters.pressureLocation,
        useMudPits: obj.useMudPits,
        mudPits: obj.useMudPits ? obj.mudPits : null,
        outsideDiameter: obj.riser?.outsideDiameter,
        insideDiameter: obj.riser?.insideDiameter,
        includeRiser: obj.includeRiser,
        annulusIndex: obj.annulusIndex
      };
    },
    fromFormGroup: (formValue: CirculationOperationForm): CirculationOperation | null => {
      const { fluid, standardFluidFlow, circulationDirection, circulationMd, inletTemperature, pressure, pressureLocation, mudPits, annulusIndex } = formValue;

      const controls = this.form.formGroup.controls;
      const check = (control, field) => control?.invalid || (control?.touched && this._dataBackup?.[field] === formValue[field]);

      if (check(controls.pressure, 'pressure') || 
          check(controls.inletTemperature, 'inletTemperature')) {
        
        controls.pressure.markAsUntouched();
        controls.inletTemperature.markAsUntouched();
        return;
      }

      this._dataBackup = formValue;

        return standardFluidFlow ? {
          type: OperationType.CIRCULATION,
          fluid,
          flow: standardFluidFlow,
          circulationParameters: {
            circulationDirection,
            circulationMd: +circulationMd,
            inletTemperature: +inletTemperature,
            pressure: +pressure,
            pressureLocation
          },
          useMudPits: formValue.useMudPits,
          mudPits: formValue.useMudPits ? mudPits: null,
          includeRiser: formValue.includeRiser ?? false,
          riser: formValue.includeRiser ? {
            outsideDiameter: formValue.outsideDiameter,
            insideDiameter: formValue.insideDiameter
          } : null,
          annulusIndex
        } : null;
      }
    });

    public fluidChangedEvent(e) {
      const state = e.value.state;
      this.fluidChanged.emit({ hasGasProperty: state.gasComposition != undefined || state.type == 'co2Fluid' || state.type == 'vle',
          hasOilProperty: state.oilApiGravity > 0 || state.type == 'vle',
          hasWaterMudProperty: state.nominalDensity != undefined || state.type == 'blackOil' || state.type == 'vle' });
    }

  ngOnChanges(): void {
    this.tempUnit = this.userUnits.temperature;
    this.pressureUnit = unitsLib[this.userUnits.pressure].symbol
    this.depthUnit = this.userUnits.longLengths;
    this.pipeRoughnessUnit = this.userUnits.shortLengths;
    const weightUnit = unitsLib[this.userUnits.linearDensity].symbol;

    const drillPipe = this.drillString['drillPipe'];
    this.selectedLandingString = drillPipe ?
      `Landing String: OD: ${drillPipe.outsideDiameter.toFixed(2)}${this.pipeRoughnessUnit}, Weight: ${drillPipe.weightPerFoot.toFixed(2)}${weightUnit}, ID: ${drillPipe.insideDiameter.toFixed(2)}${this.pipeRoughnessUnit}, Grade: ${drillPipe.drillPipeGrade?.name || 'Steel'}` :
      'Please Select a Landing String';

    this.landingStringMissing = !drillPipe;

    switch (this.tempUnit) {
      case '°F':
        this.temperatureValidation = { min: 0, max: 1000 };
        break;
      case '°C':
        this.temperatureValidation = { min: -17.78, max: 537.77 };
        break;
      case 'K':
        this.temperatureValidation = { min: 255.37, max: 810.92 };
        break;
    }
    this.form.formGroup.controls.inletTemperature.setValidators([Validators.min(this.temperatureValidation.min), Validators.max(this.temperatureValidation.max), Validators.required]);

    switch (this.userUnits.pressure) {
      case 'psi':
        this.pressureValidation = { min: 0, max: 100000 };
        break;
      case 'bar':
        this.pressureValidation = { min: 0, max: 6894 };
        break;
      case 'KPa':
        this.pressureValidation = { min: 0, max: 689475 };
        break;
      case 'atm':
        this.pressureValidation = { min: 0, max: 6804 };
        break;
    }
    this.form.formGroup.controls.pressure.setValidators([Validators.min(this.pressureValidation.min), Validators.max(this.pressureValidation.max), Validators.required]);
    this.form.formGroup.controls.circulationMd.setValidators([Validators.max(this.maxCircDepth)]);
  }

    public setDefaultSurfaceTemperature() {
    if (this.form.formGroup.controls.inletTemperature.value == null) {
      this.form.formGroup.controls.inletTemperature.setValue(this.surfaceTemperature, { emitEvent: false });
      this._dataBackup.inletTemperature = this.surfaceTemperature;
    }
  }

  ngAfterViewInit(): void {
    const controls = this.form.formGroup.controls;
    if (!controls.fluid.value) {
      controls.fluid.setValue(this.fluids ? this.fluids[0] : {
        id: "0000-0000-0000-0000",
        isInUse: false,
        state: {
          brineType: "seawater",
          nominalDensity: 10,
          plasticViscosity: 1,
          viscosityTemperature: 120,
          name: "10ppg SW",
          type: FluidType.BRINE
        }
      });
    }
    if (!isNumber(controls.circulationMd.value)) { this.form.formGroup.controls.circulationMd.setValue(this.maxCircDepth); }
    if (!isNumber(controls.inletTemperature.value)) { this.form.formGroup.controls.inletTemperature.setValue(this.surfaceTemperature); }

    if (this.defaultRiser.includeRiser && this.form.formGroup.get('includeRiser').value == null) {
      this.form.formGroup.get('includeRiser').setValue(true, { emitEvent: false });
      this.showRiser = true;
      this.checkRiserValues();
    }

    if (this.isSubsea && this.hangerMd == 0) {
      this.form.formGroup.controls.pressureLocation.setValue('pump', { emitEvent: false });
      this.form.formGroup.controls.pressureLocation.disable({ emitEvent: false });
    }
  }

  public hasRiserChange() {
    this.showRiser = !this.showRiser;
    this.checkRiserValues();
  }

  private checkRiserValues() {
    this.form.formGroup.controls.outsideDiameter
      .setValue((this.form.formGroup.value.outsideDiameter ?? this.defaultRiser?.riser?.outsideDiameter ?? GetShortLengthValueFromInches(21, this.userUnits.shortLengths)), { emitEvent: false });

    this.form.formGroup.controls.insideDiameter
      .setValue((this.form.formGroup.value.insideDiameter ?? this.defaultRiser?.riser?.insideDiameter ?? GetShortLengthValueFromInches(19.75, this.userUnits.shortLengths)));
  }
}
